/**
 * Inline chat (host): a Chat-Eingabe form with `data-dhsv-chat-expand="inline"`
 * opens the chat inside the page flow instead of a modal or drawer
 * (docs/concepts/chat-ux-handoff, 01-architektur.md section 4.6, 03 section 5.5,
 * 05-einstiege.md section 3).
 *
 * There is one chat iframe. For the inline mode it is positioned
 * `position: absolute` in document coordinates over the form, so it scrolls
 * with the page on the compositor (no per-frame following). The page space is
 * reserved by an in-flow spacer right after the form ("slot"), whose height
 * springs from 0 to `expanded - field`; the iframe is sized to the expanded
 * height from the start and a `clip-path` shows only the part the slot
 * already reserves, so the chat inside renders at its final size and never
 * relays out per frame. The form stays where it is, inert, under the iframe;
 * collapsing reveals it again.
 *
 * Pure helpers first (unit-tested), then the DOM controller.
 */
import { clampRadius } from './entry-surfaces';
import type { SpringParams } from './spring';
import { SPRING_LARGE, frameDt, smoothstep } from './spring';

/** On a `form[data-dhsv-chat-form]`: `inline` opens the chat in the page flow. */
export const INLINE_EXPAND_ATTRIBUTE = 'data-dhsv-chat-expand';
/** Class of the in-flow spacer that reserves the chat's height. */
export const INLINE_SLOT_CLASS = 'dhsv-chat-inline-slot';
/** Expanded height: `min(620, max(360, vh - 110))` (03 section 5.5). */
export const INLINE_MAX_HEIGHT_PX = 620;
export const INLINE_MIN_HEIGHT_PX = 360;
export const INLINE_VIEWPORT_MARGIN_PX = 110;
/** Corner radius of the open panel, at most (prototype: 32 - 6 = 26 px). */
export const INLINE_PANEL_MAX_RADIUS_PX = 26;
/** The chat fades in over the first part of the expansion (progress 0..this). */
export const INLINE_FADE_TO = 0.35;
/** Stacking of the inline iframe: page content, under sticky headers. */
export const INLINE_Z_INDEX = 1;
/** Gap kept to the viewport edge when the open panel is scrolled into view. */
export const INLINE_SCROLL_MARGIN_PX = 16;

const EPS_POS = 0.3;
const EPS_VEL = 3;
const SUBSTEP_HZ = 240;

/**
 * Whether a chat form asks for the inline mode.
 * @param form The submitted form.
 */
export function isInlineExpandForm( form: Element ): boolean {
	return (
		( form.getAttribute( INLINE_EXPAND_ATTRIBUTE ) || '' )
			.trim()
			.toLowerCase() === 'inline'
	);
}

/**
 * Height of the open inline chat: `min(620, max(360, vh - 110))`, never less
 * than the field it grows from. Pure.
 * @param viewportHeight `window.innerHeight`.
 * @param fieldHeight    Height of the form it grows from.
 */
export function inlineExpandedHeight(
	viewportHeight: number,
	fieldHeight: number
): number {
	const base = Math.min(
		INLINE_MAX_HEIGHT_PX,
		Math.max(
			INLINE_MIN_HEIGHT_PX,
			viewportHeight - INLINE_VIEWPORT_MARGIN_PX
		)
	);
	return Math.round( Math.max( base, fieldHeight ) );
}

/**
 * Corner radius at the start (the field's own, clamped to its shape) and at
 * the end (the field's, capped for the large panel). Pure.
 * @param raw         Computed `border-top-left-radius` of the field.
 * @param fieldWidth
 * @param fieldHeight
 */
export function inlineRadii(
	raw: string | null | undefined,
	fieldWidth: number,
	fieldHeight: number
): { from: number; to: number } {
	const from = clampRadius( raw, fieldWidth, fieldHeight );
	// A pill (huge radius) clamps to half the field height; the panel keeps a
	// rounded but not pill-shaped corner.
	const to = Math.min( from, INLINE_PANEL_MAX_RADIUS_PX );
	return { from, to };
}

export interface HeightSpring {
	/** Current spacer height, px. */
	h: number;
	/** Velocity, px/s. */
	v: number;
}

/**
 * Advance the slot height spring by `dt` seconds (semi-implicit Euler in
 * 240 Hz substeps, as `RectSpring`). Reduced motion jumps to the target
 * (03 section 11.1: the slot height jumps). Pure.
 * @param state   Current height and velocity.
 * @param target  Target height.
 * @param dt      Seconds.
 * @param params  Spring constants.
 * @param reduced Reduced motion.
 */
export function stepHeightSpring(
	state: HeightSpring,
	target: number,
	dt: number,
	params: SpringParams = SPRING_LARGE.h,
	reduced = false
): HeightSpring & { settled: boolean } {
	if ( reduced ) {
		return { h: target, v: 0, settled: true };
	}
	let { h, v } = state;
	const n = Math.max( 1, Math.ceil( dt * SUBSTEP_HZ ) );
	const step = dt / n;
	for ( let i = 0; i < n; i++ ) {
		const a = params.k * ( target - h ) - params.c * v;
		v += a * step;
		h += v * step;
	}
	if ( Math.abs( target - h ) < EPS_POS && Math.abs( v ) < EPS_VEL ) {
		return { h: target, v: 0, settled: true };
	}
	return { h, v, settled: false };
}

/**
 * Scroll needed so content below the slot does not jump: only when the slot
 * lies entirely above the viewport (03 section 5.5, `proto.js:1731`). Pure.
 * @param slotBottom Bottom of the slot in viewport px, before the change.
 * @param delta      Height change of the slot this frame.
 */
export function scrollCompensation(
	slotBottom: number,
	delta: number
): number {
	return slotBottom <= 0 ? delta : 0;
}

export interface TopLeft {
	top: number;
	left: number;
}

/**
 * The part of a compensation the browser has not done itself: scroll
 * anchoring (Chromium, Firefox) already moves the page when content above
 * the viewport changes height; WebKit without it leaves all of it to us.
 * Sub-pixel rests are dropped. Pure.
 * @param wanted   Compensation for this frame.
 * @param scrolled How far the page scrolled since the height changed.
 */
export function remainingCompensation(
	wanted: number,
	scrolled: number
): number {
	const rest = wanted - scrolled;
	return Math.abs( rest ) < 0.5 ? 0 : rest;
}

/**
 * New `top`/`left` for an absolutely positioned element, so that its
 * viewport position matches `target`. Works for any containing block: the
 * error between where it is and where it should be is added to the offsets it
 * has. Pure.
 * @param current Current `top`/`left` style values, px.
 * @param actual  Its current viewport rect.
 * @param target  Where it should be, viewport px.
 */
export function correctedOffset(
	current: TopLeft,
	actual: TopLeft,
	target: TopLeft
): TopLeft {
	return {
		top: current.top + ( target.top - actual.top ),
		left: current.left + ( target.left - actual.left ),
	};
}

/**
 * Expansion progress 0..1 of a spacer height. Pure.
 * @param spacer Spacer height.
 * @param full   Spacer height when fully open.
 */
export function inlineProgress( spacer: number, full: number ): number {
	if ( full <= 0 ) {
		return 1;
	}
	return Math.max( 0, Math.min( 1, spacer / full ) );
}

export interface InlineFrame {
	/** `clip-path` of the iframe. */
	clip: string;
	/** Opacity of the iframe. */
	opacity: number;
	/** Visible height, px. */
	visible: number;
	/** Current corner radius, px. */
	radius: number;
}

/**
 * The iframe's clip, opacity and radius for a spacer height: the visible
 * part is the field plus the spacer, the rest of the (expanded-height) iframe
 * is clipped. The chat fades in over the first part of the expansion, so the
 * field reads as turning into the chat, and fades out at the end of the
 * collapse. Pure.
 * @param input
 * @param input.field      Field (form) height.
 * @param input.spacer     Current spacer height.
 * @param input.expanded   Iframe (expanded) height.
 * @param input.radii      Radius at the start and the end.
 * @param input.radii.from
 * @param input.radii.to
 */
export function inlineFrame( input: {
	field: number;
	spacer: number;
	expanded: number;
	radii: { from: number; to: number };
} ): InlineFrame {
	const full = Math.max( 0, input.expanded - input.field );
	const progress = inlineProgress( input.spacer, full );
	const visible = Math.max(
		0,
		Math.min( input.expanded, input.field + Math.max( 0, input.spacer ) )
	);
	const radius =
		input.radii.from + ( input.radii.to - input.radii.from ) * progress;
	const bottom = Math.max( 0, input.expanded - visible );
	const r = Math.max( 0, Math.min( radius, visible / 2 ) );
	return {
		clip: `inset(0px 0px ${ round2( bottom ) }px 0px round ${ round2(
			r
		) }px)`,
		opacity: full > 0 ? smoothstep( 0, INLINE_FADE_TO, progress ) : 1,
		visible,
		radius: r,
	};
}

/**
 * How far to scroll so the open panel is on screen: up when its top is above
 * the viewport, down until its bottom (plus a margin) is visible but never so
 * far that its top leaves the viewport. 0 when it already fits. Pure.
 * @param rect           Panel rect, viewport px.
 * @param rect.top
 * @param rect.bottom
 * @param viewportHeight `window.innerHeight`.
 */
export function revealScroll(
	rect: { top: number; bottom: number },
	viewportHeight: number
): number {
	if ( rect.top < INLINE_SCROLL_MARGIN_PX ) {
		return rect.top - INLINE_SCROLL_MARGIN_PX;
	}
	const overflow = rect.bottom + INLINE_SCROLL_MARGIN_PX - viewportHeight;
	if ( overflow <= 0 ) {
		return 0;
	}
	const room = rect.top - INLINE_SCROLL_MARGIN_PX;
	return Math.max( 0, Math.min( overflow, room ) );
}

function round2( n: number ): number {
	return Math.round( n * 100 ) / 100;
}

// ---------------------------------------------------------------------------
// DOM controller
// ---------------------------------------------------------------------------

export interface InlineSlotOptions {
	reducedMotion?: boolean;
	/** Debug slow-motion factor (>= 1). */
	slow?: number;
	/** Called once the expansion has settled. */
	onOpened?: () => void;
}

type Phase = 'opening' | 'open' | 'closing' | 'released' | 'done';

/**
 * The inline slot of one form: spacer, spring and (while attached) the iframe
 * geometry. Created on submit, gone after the collapse.
 */
export class InlineChatSlot {
	readonly form: HTMLElement;
	readonly spacer: HTMLDivElement;
	private iframe: HTMLIFrameElement | null = null;
	private readonly reduced: boolean;
	private readonly slow: number;
	private readonly onOpened?: () => void;
	private field = 0;
	private expanded = 0;
	private radii = { from: 0, to: 0 };
	private spring: HeightSpring = { h: 0, v: 0 };
	private phase: Phase = 'opening';
	private raf: number | null = null;
	private last = 0;
	private onDone: ( () => void ) | null = null;
	private observer: ResizeObserver | null = null;
	private readonly hadInert: boolean;
	private zIndex = '';
	private readonly onResize = (): void => this.sync();

	constructor( form: HTMLElement, options: InlineSlotOptions = {} ) {
		this.form = form;
		this.reduced = options.reducedMotion === true;
		this.slow = Math.max( 1, options.slow ?? 1 );
		this.onOpened = options.onOpened;
		const rect = form.getBoundingClientRect();
		this.field = rect.height;
		this.expanded = inlineExpandedHeight( window.innerHeight, rect.height );
		this.radii = inlineRadii(
			window.getComputedStyle( form ).borderTopLeftRadius,
			rect.width,
			rect.height
		);
		const spacer = document.createElement( 'div' );
		spacer.className = INLINE_SLOT_CLASS;
		spacer.setAttribute( 'aria-hidden', 'true' );
		spacer.style.height = '0px';
		spacer.style.margin = '0';
		spacer.style.padding = '0';
		form.insertAdjacentElement( 'afterend', spacer );
		this.spacer = spacer;
		// Covered by the chat: out of the tab order and the accessibility tree.
		this.hadInert = form.hasAttribute( 'inert' );
		form.setAttribute( 'inert', '' );
	}

	/** Corner radius of the open panel, px. */
	get panelRadius(): number {
		return this.radii.to;
	}

	/** Whether the iframe is (still) bound to this slot. */
	get isAttached(): boolean {
		return this.iframe !== null;
	}

	/** Whether the slot is collapsing or gone. */
	get isClosing(): boolean {
		return (
			this.phase === 'closing' ||
			this.phase === 'released' ||
			this.phase === 'done'
		);
	}

	/**
	 * Bind the iframe to the slot and start growing.
	 * @param iframe The chat iframe.
	 */
	open( iframe: HTMLIFrameElement ): void {
		this.iframe = iframe;
		// Page content now: below the theme's sticky header and menus, not
		// above everything like the overlay iframe.
		this.zIndex = iframe.style.zIndex;
		iframe.style.zIndex = String( INLINE_Z_INDEX );
		iframe.style.position = 'absolute';
		iframe.style.inset = 'auto';
		iframe.style.top = '0px';
		iframe.style.left = '0px';
		iframe.style.pointerEvents = 'auto';
		this.phase = 'opening';
		this.apply( 0 );
		this.position();
		window.addEventListener( 'resize', this.onResize, { passive: true } );
		if ( typeof ResizeObserver === 'function' ) {
			this.observer = new ResizeObserver( () => this.sync() );
			this.observer.observe( this.form );
			this.observer.observe( document.body );
		}
		this.run();
	}

	/**
	 * Spring back to the field, then remove the spacer and call `onDone`.
	 * @param onDone Called once, after the teardown.
	 */
	collapse( onDone: () => void ): void {
		if ( this.phase === 'done' ) {
			onDone();
			return;
		}
		this.onDone = onDone;
		this.phase = 'closing';
		this.run();
	}

	/**
	 * The chat moved elsewhere (modal, drawer): let go of the iframe at once
	 * and spring the spacer back on its own.
	 */
	release(): void {
		// Whoever waited for the collapse does not any more.
		this.onDone = null;
		this.detach();
		if ( this.phase === 'done' ) {
			return;
		}
		this.phase = 'released';
		this.run();
	}

	/** Tear down at once, without animation. */
	destroy(): void {
		this.onDone = null;
		this.detach();
		this.finish();
	}

	/** Re-measure after a viewport change: new target height, new position. */
	sync(): void {
		if ( this.phase === 'done' ) {
			return;
		}
		const rect = this.form.getBoundingClientRect();
		const next = inlineExpandedHeight( window.innerHeight, rect.height );
		if ( next !== this.expanded || rect.height !== this.field ) {
			this.field = rect.height;
			this.expanded = next;
			this.run();
		}
		this.apply( this.spring.h );
		this.position();
	}

	/** Scroll the open panel into view, if it sticks out below. */
	reveal(): void {
		if ( ! this.iframe ) {
			return;
		}
		const top = this.form.getBoundingClientRect().top;
		const by = revealScroll(
			{ top, bottom: top + this.expanded },
			window.innerHeight
		);
		if ( by !== 0 ) {
			window.scrollBy( {
				top: by,
				behavior: this.reduced ? 'auto' : 'smooth',
			} );
		}
	}

	private target(): number {
		return this.phase === 'opening' || this.phase === 'open'
			? Math.max( 0, this.expanded - this.field )
			: 0;
	}

	private run(): void {
		if ( this.raf !== null ) {
			return;
		}
		if ( this.reduced ) {
			this.tick( 0 );
			return;
		}
		this.last = performance.now();
		this.raf = window.requestAnimationFrame( ( now ) => this.frame( now ) );
	}

	private frame( now: number ): void {
		this.raf = null;
		const dt = frameDt( now, this.last, this.slow );
		this.last = now;
		if ( ! this.tick( dt ) ) {
			this.raf = window.requestAnimationFrame( ( t ) => this.frame( t ) );
		}
	}

	/**
	 * One spring step. Returns true once settled (and handled).
	 * @param dt Seconds.
	 */
	private tick( dt: number ): boolean {
		const target = this.target();
		const before = this.spring.h;
		const next = stepHeightSpring(
			this.spring,
			target,
			dt,
			SPRING_LARGE.h,
			this.reduced
		);
		this.spring = { h: next.h, v: next.v };
		const delta = next.h - before;
		if ( delta !== 0 ) {
			const slotBottom = this.spacer.getBoundingClientRect().bottom;
			const scrollBefore = window.scrollY;
			this.apply( next.h );
			const by = scrollCompensation( slotBottom, delta );
			if ( by !== 0 ) {
				// Layout now: a browser with scroll anchoring has already
				// compensated (part of) the change; only the rest is ours.
				this.spacer.getBoundingClientRect();
				const rest = remainingCompensation(
					by,
					window.scrollY - scrollBefore
				);
				if ( rest !== 0 ) {
					// Instant even under a theme's `scroll-behavior: smooth`,
					// or the compensation lags behind the slot.
					window.scrollBy( { top: rest, behavior: 'instant' } );
				}
			}
			this.position();
		}
		if ( ! next.settled ) {
			return false;
		}
		if ( this.phase === 'opening' ) {
			this.phase = 'open';
			this.onOpened?.();
		} else if ( this.phase === 'closing' || this.phase === 'released' ) {
			this.detach();
			this.finish();
		}
		return true;
	}

	/**
	 * Write the spacer height and the iframe's size, clip and opacity.
	 * @param spacer Spacer height.
	 */
	private apply( spacer: number ): void {
		this.spacer.style.height = `${ Math.max( 0, spacer ) }px`;
		const iframe = this.iframe;
		if ( ! iframe ) {
			return;
		}
		const frame = inlineFrame( {
			field: this.field,
			spacer,
			expanded: this.expanded,
			radii: this.radii,
		} );
		iframe.style.height = `${ this.expanded }px`;
		iframe.style.clipPath = frame.clip;
		iframe.style.opacity = String( round2( frame.opacity ) );
	}

	/** Put the iframe over the form (document coordinates). */
	private position(): void {
		const iframe = this.iframe;
		if ( ! iframe || this.phase === 'done' ) {
			return;
		}
		const target = this.form.getBoundingClientRect();
		iframe.style.width = `${ target.width }px`;
		const actual = iframe.getBoundingClientRect();
		const next = correctedOffset(
			{
				top: parseFloat( iframe.style.top ) || 0,
				left: parseFloat( iframe.style.left ) || 0,
			},
			actual,
			target
		);
		iframe.style.top = `${ round2( next.top ) }px`;
		iframe.style.left = `${ round2( next.left ) }px`;
	}

	/** Let go of the iframe: its next geometry is the host's to set. */
	private detach(): void {
		const iframe = this.iframe;
		this.iframe = null;
		this.observer?.disconnect();
		this.observer = null;
		window.removeEventListener( 'resize', this.onResize );
		if ( iframe ) {
			clearInlineIframeStyles( iframe );
			iframe.style.zIndex = this.zIndex;
		}
	}

	private finish(): void {
		if ( this.raf !== null ) {
			window.cancelAnimationFrame( this.raf );
			this.raf = null;
		}
		if ( this.phase !== 'done' ) {
			this.phase = 'done';
			this.spacer.remove();
			if ( ! this.hadInert ) {
				this.form.removeAttribute( 'inert' );
			}
		}
		const done = this.onDone;
		this.onDone = null;
		done?.();
	}
}

/**
 * Remove what the inline mode wrote on the iframe, besides the geometry the
 * host always rewrites.
 * @param iframe The chat iframe.
 */
export function clearInlineIframeStyles( iframe: HTMLIFrameElement ): void {
	iframe.style.clipPath = '';
	iframe.style.opacity = '';
}
